import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  FiGrid,
  FiGift,
  FiUsers,
  FiBox,
  FiBell,
  FiHelpCircle,
  FiChevronRight,
  FiStar,
  FiAward,
  FiPercent,
  FiCopy,
  FiShare2,
  FiCheck,
  FiTrendingUp,
  FiShoppingBag,
  FiCoffee,
  FiHome,
  FiZap,
  FiHeart,
  FiShield,
  FiMessageCircle,
  FiPlayCircle,
  FiBookOpen,
  FiThumbsUp,
  FiLoader,
  FiArrowRight,
  FiExternalLink,
  FiClock,
  FiTarget,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Academia from './Academia';
import Missions from '../components/Hub/Missions';
import { hubService } from '../services';
import type {
  Parceiro as ParceiroDB,
  BeneficiosData,
  ReferralData,
  MiniApp as MiniAppDB,
  Novidade as NovidadeDB,
  FeatureRequest,
  Recurso,
  NivelBeneficio,
} from '../services/hub.service';

// Types
interface Parceiro {
  id: number;
  nome: string;
  categoria: string;
  desconto: string;
  logo: string;
  destaque?: boolean;
}

interface MiniApp {
  id: number;
  nome: string;
  descricao: string;
  icone: React.ReactNode;
  categoria: string;
  preco: number;
  preco_original?: number;
  gratuito?: boolean;
  comprado?: boolean;
}

interface Novidade {
  id: number;
  titulo: string;
  descricao: string;
  data: string;
  tipo: 'feature' | 'update' | 'promo';
}

// Empty fallbacks (data comes from API/Admin)
const parceiros: Parceiro[] = [];
const miniApps: MiniApp[] = [];
const novidades: Novidade[] = [];

// Minimal default for when the API returns no niveis at all (first boot / DB empty)
const defaultNivel: NivelBeneficio = {
  id: 0,
  nome: 'Bronze',
  pontos_minimos: 0,
  pontos_maximos: 999,
  cor: 'from-amber-600 to-amber-700',
  icone: 'award',
  beneficios: ['Descontos basicos', 'Suporte standard'],
};

// Helper to get icon component from icon name string
const getAppIcon = (iconName: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    'shield': <FiShield className="text-blue-500" />,
    'coffee': <FiCoffee className="text-amber-500" />,
    'zap': <FiZap className="text-green-500" />,
    'star': <FiStar className="text-purple-500" />,
    'box': <FiBox className="text-red-500" />,
    'home': <FiHome className="text-teal-500" />,
    'heart': <FiHeart className="text-pink-500" />,
    'shopping-bag': <FiShoppingBag className="text-orange-500" />,
  };
  return iconMap[iconName] || <FiBox className="text-gray-500" />;
};

const Hub: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'academia');
  const [referralCopied, setReferralCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [novidadesFilter, setNovidadesFilter] = useState<'all' | 'feature' | 'update' | 'promo'>('all');
  const [parceiroCategoriaFilter, setParceiroCategoriaFilter] = useState<string>('Todos');
  const [appCategoriaFilter, setAppCategoriaFilter] = useState<string>('Todos');
  const [applyCodeInput, setApplyCodeInput] = useState('');
  const [applyCodeLoading, setApplyCodeLoading] = useState(false);
  const [applyCodeMsg, setApplyCodeMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // API Data State
  const [parceirosData, setParceirosData] = useState<ParceiroDB[]>([]);
  const [parceirosCategorias, setParceirosCategorias] = useState<string[]>(['Todos']);
  const [beneficiosData, setBeneficiosData] = useState<BeneficiosData | null>(null);
  const [referralDataState, setReferralDataState] = useState<ReferralData | null>(null);
  const [appsData, setAppsData] = useState<MiniAppDB[]>([]);
  const [appsCategorias, setAppsCategorias] = useState<string[]>(['Todos']);
  const [novidadesData, setNovidadesData] = useState<NovidadeDB[]>([]);
  const [featuresData, setFeaturesData] = useState<FeatureRequest[]>([]);
  const [recursosData, setRecursosData] = useState<Recurso[]>([]);
  const [hubMe, setHubMe] = useState<{ is_embaixador: boolean; embaixador: any } | null>(null);

  // Derived from API
  const todosNiveis: NivelBeneficio[] = beneficiosData?.todos_niveis?.length ? beneficiosData.todos_niveis : [defaultNivel];
  const userPontos = beneficiosData?.total_pontos ?? 0;
  const userNivel: NivelBeneficio = beneficiosData?.nivel_atual ?? defaultNivel;
  const nextNivel: NivelBeneficio | null = beneficiosData?.proximo_nivel ?? null;
  const progressoNivel = beneficiosData?.progresso_percentual ?? 0;

  const referralCode = referralDataState?.codigo ?? `PAGALI-${user?.id?.toString().slice(-4) || 'USER'}`;
  const referralLink = referralDataState?.link ?? `https://pagali.cv/r/${referralCode}`;
  const totalReferrals = referralDataState?.total_convites ?? 0;
  const referralBonus = referralDataState?.total_bonus ?? 0;
  const bonusPorConvite = referralDataState?.bonus_por_convite ?? 500;
  const bonusConvidado = referralDataState?.bonus_convidado ?? 250;

  // Fetch data from API (progressive: cada endpoint atualiza o seu estado
  // assim que responde, em vez de esperar pelo batch todo).
  const fetchData = useCallback(async () => {
    setLoading(true);

    const safe = <T,>(p: Promise<T>, onOk: (v: T) => void) =>
      p.then(onOk).catch((err) => console.warn('[Hub] fetch failed:', err?.message));

    // Fire all requests in parallel — each setter runs as soon as its response arrives.
    const tasks = [
      safe(hubService.getParceiros(), setParceirosData),
      safe(hubService.getParceirosCategorias(), setParceirosCategorias),
      safe(hubService.getBeneficios(), setBeneficiosData),
      safe(hubService.getReferral(), setReferralDataState),
      safe(hubService.getApps(), setAppsData),
      safe(hubService.getAppsCategorias(), setAppsCategorias),
      safe(hubService.getNovidades(), setNovidadesData),
      safe(hubService.getFeatures(), setFeaturesData),
      safe(hubService.getRecursos(), setRecursosData),
      safe(hubService.getMe(), setHubMe),
    ];

    // Hide the global loading indicator as soon as the FIRST response arrives —
    // tabs render whatever data is available, and empty states handle the rest.
    Promise.race(tasks).finally(() => setLoading(false));

    // Still await all so callers can .then() on fetchData (e.g. after vote/apply-code).
    await Promise.allSettled(tasks);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync tab when URL param changes (e.g. sidebar link clicked while already on /hub)
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Use API data or fallback to mock data
  const displayParceiros = parceirosData.length > 0
    ? parceirosData.map(p => ({ ...p, destaque: p.destaque }))
    : parceiros;

  const displayApps = appsData.length > 0
    ? appsData.map(a => ({
        id: a.id,
        nome: a.nome,
        descricao: a.descricao,
        icone: getAppIcon(a.icone),
        categoria: a.categoria,
        preco: a.preco,
        preco_original: a.preco_original,
        gratuito: a.gratuito,
        comprado: a.purchased,
      }))
    : miniApps;

  const displayNovidades = novidadesData.length > 0
    ? novidadesData.map(n => ({
        id: n.id,
        titulo: n.titulo,
        descricao: n.resumo,
        data: new Date(n.published_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }),
        tipo: n.categoria as 'feature' | 'update' | 'promo',
      }))
    : novidades;

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralCode);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
  };

  const handleApplyReferralCode = async () => {
    const code = applyCodeInput.trim().toUpperCase();
    if (!code) return;
    setApplyCodeLoading(true);
    setApplyCodeMsg(null);
    try {
      const result = await hubService.aplicarCodigoReferral(code);
      setApplyCodeMsg({
        type: 'success',
        text: result.message || `Código aplicado! +${result.bonus || 250} pontos creditados.`,
      });
      setApplyCodeInput('');
      // Refresh data to show updated points
      fetchData();
    } catch (err: any) {
      setApplyCodeMsg({
        type: 'error',
        text: err?.message || 'Erro ao aplicar código',
      });
    } finally {
      setApplyCodeLoading(false);
    }
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Junta-te ao Pagali!',
        text: `Usa o meu codigo ${referralCode} e ganha bonus!`,
        url: referralLink,
      });
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const tabs = [
    { id: 'academia', label: 'Academia', icon: <FiBookOpen /> },
    { id: 'missoes', label: 'Missões', icon: <FiTarget /> },
    { id: 'fidelidade', label: 'Fidelidade', icon: <FiGrid /> },
    { id: 'parceiros', label: t('hub.partners'), icon: <FiShoppingBag /> },
    { id: 'beneficios', label: t('hub.benefits'), icon: <FiGift /> },
    { id: 'referral', label: t('hub.invite'), icon: <FiUsers /> },
    { id: 'apps', label: t('hub.miniApps'), icon: <FiBox /> },
    { id: 'novidades', label: t('hub.news'), icon: <FiBell /> },
    { id: 'recursos', label: t('hub.resources'), icon: <FiHelpCircle /> },
  ];

  const formatRelativeDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Hoje';
    if (diffDays === 1) return 'Ontem';
    if (diffDays < 7) return `Há ${diffDays} dias`;
    if (diffDays < 30) return `Há ${Math.floor(diffDays / 7)} semana${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
    return date.toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const novidadeStyle = (tipo: string) => {
    switch (tipo) {
      case 'feature':
        return {
          badge: 'Nova funcionalidade',
          bg: 'bg-purple-100',
          text: 'text-purple-700',
          icon: <FiStar className="text-purple-600" />,
          gradient: 'from-purple-500 to-indigo-600',
        };
      case 'promo':
        return {
          badge: 'Promoção',
          bg: 'bg-emerald-100',
          text: 'text-emerald-700',
          icon: <FiGift className="text-emerald-600" />,
          gradient: 'from-emerald-500 to-teal-600',
        };
      case 'update':
      default:
        return {
          badge: 'Atualização',
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          icon: <FiTrendingUp className="text-blue-600" />,
          gradient: 'from-blue-500 to-cyan-600',
        };
    }
  };

  const filteredNovidades = novidadesFilter === 'all'
    ? novidadesData
    : novidadesData.filter(n => n.categoria === novidadesFilter);

  const featuredNovidade = novidadesData.find(n => Number(n.destaque) === 1) || novidadesData[0];

  const getCategoriaIcon = (categoria: string) => {
    switch (categoria.toLowerCase()) {
      case 'alimentacao': return <FiShoppingBag className="text-green-500" />;
      case 'saude': return <FiHeart className="text-red-500" />;
      case 'restaurantes': return <FiCoffee className="text-amber-500" />;
      case 'telecoms': return <FiZap className="text-blue-500" />;
      case 'servicos': return <FiHome className="text-purple-500" />;
      case 'moda': return <FiStar className="text-pink-500" />;
      default: return <FiBox className="text-gray-500" />;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
            <FiGrid className="text-2xl text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{t('hub.title')}</h1>
              {hubMe?.is_embaixador && hubMe.embaixador && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500 text-white shadow-sm">
                  <span className="text-base leading-none">{hubMe.embaixador.badge_icone || '⭐'}</span>
                  Embaixador {hubMe.embaixador.tier.toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-gray-500">{t('hub.subtitle')}</p>
            {hubMe?.is_embaixador && hubMe.embaixador && (
              <p className="text-xs text-purple-700 font-medium mt-1">
                ✨ Recebes {hubMe.embaixador.pontos_mensais} pontos todos os meses
                {hubMe.embaixador.early_access ? ' + acesso antecipado a novas features' : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon}
            <span className="text-sm font-medium">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Subtle top progress bar while any hub data is still loading */}
      {loading && (
        <div className="relative h-0.5 mb-4 bg-indigo-100 rounded-full overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full animate-pulse" style={{ width: '40%' }} />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'academia' && <Academia />}
      {activeTab === 'missoes' && <Missions />}

      {activeTab === 'fidelidade' && (
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
              <FiAward className="text-2xl mb-2 opacity-80" />
              <p className="text-2xl font-bold">{userPontos.toLocaleString()}</p>
              <p className="text-sm opacity-80">{t('hub.pagaliPoints')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
              <FiStar className="text-2xl mb-2 text-amber-500" />
              <p className="text-2xl font-bold text-gray-900">{userNivel.nome}</p>
              <p className="text-sm text-gray-500">{t('hub.currentLevel')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
              <FiUsers className="text-2xl mb-2 text-green-500" />
              <p className="text-2xl font-bold text-gray-900">{totalReferrals}</p>
              <p className="text-sm text-gray-500">{t('hub.friendsInvited')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
              <FiPercent className="text-2xl mb-2 text-blue-500" />
              <p className="text-2xl font-bold text-gray-900">{displayParceiros.length}</p>
              <p className="text-sm text-gray-500">{t('hub.partnersCount')}</p>
            </div>
          </div>

          {/* Progress to Next Level */}
          {nextNivel && (
            <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-500">Progresso para {nextNivel.nome}</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {userPontos.toLocaleString()} / {nextNivel.pontos_minimos.toLocaleString()} pontos
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${nextNivel.cor} text-white text-sm font-medium`}>
                  {nextNivel.nome}
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`bg-gradient-to-r ${userNivel.cor} h-3 rounded-full transition-all`}
                  style={{ width: `${progressoNivel}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Faltam {Math.max(0, nextNivel.pontos_minimos - userPontos).toLocaleString()} pontos
              </p>
            </div>
          )}

          {/* Featured Partners */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Parceiros em Destaque</h2>
              <button onClick={() => setActiveTab('parceiros')} className="text-sm text-indigo-600 flex items-center gap-1">
                Ver todos <FiChevronRight />
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {displayParceiros.filter(p => p.destaque).map((parceiro) => (
                <div key={parceiro.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                      {getCategoriaIcon(parceiro.categoria)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{parceiro.nome}</h3>
                      <p className="text-xs text-gray-500">{parceiro.categoria}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Desconto</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {parceiro.desconto} OFF
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Latest News */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Novidades</h2>
              <button onClick={() => setActiveTab('novidades')} className="text-sm text-indigo-600 flex items-center gap-1">
                Ver todas <FiChevronRight />
              </button>
            </div>
            <div className="space-y-3">
              {displayNovidades.slice(0, 2).map((novidade) => (
                <div key={novidade.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    novidade.tipo === 'feature' ? 'bg-purple-100' :
                    novidade.tipo === 'promo' ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    {novidade.tipo === 'feature' ? <FiStar className="text-purple-600" /> :
                     novidade.tipo === 'promo' ? <FiGift className="text-green-600" /> : <FiTrendingUp className="text-blue-600" />}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{novidade.titulo}</h3>
                    <p className="text-sm text-gray-500">{novidade.descricao}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'parceiros' && (
        <div className="space-y-6">
          {/* Categories Filter (from API) */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {parceirosCategorias.map((cat) => (
              <button
                key={cat}
                onClick={() => setParceiroCategoriaFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                  parceiroCategoriaFilter === cat
                    ? 'bg-indigo-600 text-white border border-indigo-600'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Partners Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayParceiros
              .filter(p => parceiroCategoriaFilter === 'Todos' || p.categoria === parceiroCategoriaFilter)
              .map((parceiro) => (
              <div key={parceiro.id} className={`bg-white rounded-xl p-5 border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${parceiro.is_fundador && parceiro.badge_visivel ? 'border-amber-300 ring-2 ring-amber-200' : 'border-gray-100'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                    {getCategoriaIcon(parceiro.categoria)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{parceiro.nome}</h3>
                    <p className="text-sm text-gray-500">{parceiro.categoria}</p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {parceiro.is_fundador && parceiro.badge_visivel && (
                      <span className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-xs font-bold shadow-sm">
                        🏆 Fundador{parceiro.cidade_fundador ? ` ${parceiro.cidade_fundador}` : ''}
                      </span>
                    )}
                    {parceiro.destaque && !parceiro.is_fundador && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                        Destaque
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-sm text-gray-500">Desconto exclusivo</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                    {parceiro.desconto} OFF
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'beneficios' && (
        <div className="space-y-6">
          {/* Current Level Card */}
          <div className={`bg-gradient-to-r ${userNivel.cor} rounded-2xl p-6 text-white`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm opacity-80">O teu nivel</p>
                <h2 className="text-3xl font-bold">{userNivel.nome}</h2>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-80">Pontos</p>
                <p className="text-2xl font-bold">{userPontos.toLocaleString()}</p>
              </div>
            </div>
            {/* Cashback + partner discount highlight */}
            <div className="grid grid-cols-2 gap-3 my-4">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                <p className="text-xs opacity-75">Cashback Wallet</p>
                <p className="text-2xl font-bold">
                  {userNivel.cashback_percent ?? 0}%
                </p>
                <p className="text-xs opacity-75">em cada pagamento</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                <p className="text-xs opacity-75">Desconto Parceiros</p>
                <p className="text-2xl font-bold">
                  +{userNivel.desconto_parceiros_percent ?? 0}%
                </p>
                <p className="text-xs opacity-75">bonus fidelidade</p>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Beneficios do teu nivel:</p>
              <div className="flex flex-wrap gap-2">
                {userNivel.beneficios.map((b: string, i: number) => (
                  <span key={i} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* All Levels */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Todos os Niveis</h3>
            <div className="space-y-4">
              {todosNiveis.map((nivel) => (
                <div
                  key={nivel.id || nivel.nome}
                  className={`bg-white rounded-xl p-5 border ${
                    nivel.nome === userNivel.nome ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${nivel.cor} flex items-center justify-center`}>
                        <FiAward className="text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{nivel.nome}</h4>
                        <p className="text-sm text-gray-500">{nivel.pontos_minimos.toLocaleString()}+ pontos</p>
                      </div>
                    </div>
                    {nivel.nome === userNivel.nome && (
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                        Atual
                      </span>
                    )}
                    {userPontos < nivel.pontos_minimos && (
                      <span className="text-sm text-gray-400">
                        Faltam {(nivel.pontos_minimos - userPontos).toLocaleString()} pts
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(nivel.cashback_percent ?? 0) > 0 && (
                      <span className="px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                        💰 Cashback {nivel.cashback_percent}%
                      </span>
                    )}
                    {(nivel.desconto_parceiros_percent ?? 0) > 0 && (
                      <span className="px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full text-xs font-semibold">
                        🏷️ +{nivel.desconto_parceiros_percent}% parceiros
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {nivel.beneficios.map((b: string, i: number) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How to Earn Points */}
          <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
            <h3 className="font-semibold text-gray-900 mb-3">Como ganhar pontos?</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <span className="text-sm text-gray-700">10 ECV pagos pela Wallet = 1 ponto</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <span className="text-sm text-gray-700">100 ECV carregados na Wallet = 1 ponto</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <span className="text-sm text-gray-700">Convidar amigo = +{bonusPorConvite} pontos</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <span className="text-sm text-gray-700">Aceitar convite de amigo = +{bonusConvidado} pontos</span>
              </div>
              <div className="flex items-center gap-3">
                <FiCheck className="text-green-500" />
                <span className="text-sm text-gray-700">Comprar mini-app = 1 ponto por cada 10 ECV</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'referral' && (
        <div className="space-y-6">
          {/* Referral Stats */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
            <h2 className="text-xl font-bold mb-4">Convida & Ganha</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white/20 rounded-xl p-4">
                <p className="text-3xl font-bold">{totalReferrals}</p>
                <p className="text-sm opacity-80">Amigos convidados</p>
              </div>
              <div className="bg-white/20 rounded-xl p-4">
                <p className="text-3xl font-bold">{referralBonus.toLocaleString()}</p>
                <p className="text-sm opacity-80">ECV ganhos</p>
              </div>
            </div>

            {/* Referral Code */}
            <div className="bg-white rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-2">O teu codigo de convite</p>
              <div className="flex items-center gap-2">
                <span className="flex-1 text-xl font-bold text-gray-900 font-mono">{referralCode}</span>
                <button
                  onClick={copyReferralCode}
                  className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {referralCopied ? <FiCheck className="text-green-600" /> : <FiCopy className="text-gray-600" />}
                </button>
                <button
                  onClick={shareReferral}
                  className="p-2 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors"
                >
                  <FiShare2 className="text-indigo-600" />
                </button>
              </div>
            </div>
          </div>

          {/* How it Works */}
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-4">Como funciona?</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-600 font-semibold">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Partilha o teu codigo</p>
                  <p className="text-sm text-gray-500">Envia o codigo ou link aos teus amigos</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-600 font-semibold">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Amigo regista-se</p>
                  <p className="text-sm text-gray-500">O teu amigo cria conta com o teu codigo</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-semibold">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Ambos ganham!</p>
                  <p className="text-sm text-gray-500">Tu ganhas {bonusPorConvite} ECV e o teu amigo {bonusConvidado} ECV</p>
                </div>
              </div>
            </div>
          </div>

          {/* Apply Referral Code */}
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-1">Tens um código de convite?</h3>
            <p className="text-sm text-gray-500 mb-4">Introduz o código de um amigo e ganha {bonusConvidado} pontos de boas-vindas.</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="PAGALI-XXXX"
                value={applyCodeInput}
                onChange={(e) => setApplyCodeInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyReferralCode(); }}
                disabled={applyCodeLoading}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 font-mono uppercase focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-50"
              />
              <button
                onClick={handleApplyReferralCode}
                disabled={applyCodeLoading || !applyCodeInput.trim()}
                className="px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {applyCodeLoading ? <FiLoader className="animate-spin" /> : 'Aplicar'}
              </button>
            </div>
            {applyCodeMsg && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${
                applyCodeMsg.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {applyCodeMsg.text}
              </div>
            )}
          </div>

          {/* Meus Convites */}
          {referralDataState?.convites && referralDataState.convites.length > 0 && (
            <div className="bg-white rounded-xl p-5 border border-gray-100">
              <h3 className="font-semibold text-gray-900 mb-4">Os meus convites</h3>
              <div className="space-y-3">
                {referralDataState.convites.map((convite, idx) => (
                  <div key={convite.id || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <FiUsers className="text-green-600 text-sm" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{convite.convidado_nome}</p>
                      <p className="text-xs text-gray-500">{convite.convidado_email}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {convite.created_at ? new Date(convite.created_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short' }) : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'apps' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
            <h2 className="text-xl font-bold mb-2">Mini-Apps Store</h2>
            <p className="text-sm opacity-80">Expande as funcionalidades do teu Pagali com apps especializados</p>
          </div>

          {/* Categories (from API) */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {appsCategorias.map((cat) => (
              <button
                key={cat}
                onClick={() => setAppCategoriaFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                  appCategoriaFilter === cat
                    ? 'bg-indigo-600 text-white border border-indigo-600'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Apps Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayApps
              .filter(a => appCategoriaFilter === 'Todos' || a.categoria === appCategoriaFilter)
              .map((app) => (
              <div key={app.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-3 bg-gray-100 rounded-xl">
                    {app.icone}
                  </div>
                  <div className="text-right">
                    {app.comprado ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Adquirido
                      </span>
                    ) : app.gratuito ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        Gratis
                      </span>
                    ) : app.preco_original ? (
                      <div className="flex flex-col items-end">
                        <span className="text-xs text-gray-400 line-through">{app.preco_original} ECV</span>
                        <span className="text-sm font-bold text-green-600">{app.preco} ECV</span>
                      </div>
                    ) : (
                      <span className="text-sm font-bold text-gray-900">{app.preco} ECV</span>
                    )}
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{app.nome}</h3>
                <p className="text-sm text-gray-500 mb-3">{app.descricao}</p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{app.categoria}</span>
                  {app.comprado ? (
                    <button
                      onClick={async () => {
                        try {
                          const result = await hubService.openApp(app.id);
                          if (result.url) window.open(result.url, '_blank');
                        } catch (error) {
                          console.error('Error opening app:', error);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                    >
                      Abrir
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          await hubService.purchaseApp(app.id);
                          fetchData();
                        } catch (error) {
                          console.error('Error purchasing app:', error);
                        }
                      }}
                      className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      {app.gratuito ? 'Obter Gratis' : `Comprar ${app.preco} ECV`}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'novidades' && (
        <div className="space-y-6">
          {/* Hero Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-6 md:p-8 text-white">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-16 -right-16 h-60 w-60 rounded-full bg-white/5 blur-3xl" />
            <div className="relative">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
                <FiBell /> Novidades Pagali
              </div>
              <h2 className="mb-2 text-2xl font-bold md:text-3xl">O que há de novo no teu Pagali</h2>
              <p className="max-w-xl text-sm text-white/85 md:text-base">
                Descobre as últimas funcionalidades, atualizações e promoções da plataforma.
              </p>
            </div>
          </div>

          {/* Featured highlight card */}
          {featuredNovidade && (() => {
            const style = novidadeStyle(featuredNovidade.categoria);
            return (
              <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-lg">
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${style.gradient}`} />
                <div className="flex flex-col gap-5 p-6 md:flex-row md:items-center md:p-8">
                  <div className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${style.gradient} text-white shadow-lg`}>
                    <div className="text-3xl">{style.icon}</div>
                  </div>
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}>
                        ⭐ Em destaque
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <FiClock className="text-xs" /> {formatRelativeDate(featuredNovidade.published_at)}
                      </span>
                    </div>
                    <h3 className="mb-1 text-xl font-bold text-gray-900 md:text-2xl">{featuredNovidade.titulo}</h3>
                    <p className="text-sm text-gray-600 md:text-base">{featuredNovidade.resumo}</p>
                  </div>
                  {featuredNovidade.link && (
                    <a
                      href={featuredNovidade.link}
                      className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${style.gradient} px-5 py-3 text-sm font-semibold text-white shadow-md transition-transform hover:scale-105`}
                    >
                      Explorar <FiArrowRight />
                    </a>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Category filter chips */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {[
              { id: 'all' as const, label: 'Todas', count: novidadesData.length },
              { id: 'feature' as const, label: 'Funcionalidades', count: novidadesData.filter(n => n.categoria === 'feature').length },
              { id: 'update' as const, label: 'Atualizações', count: novidadesData.filter(n => n.categoria === 'update').length },
              { id: 'promo' as const, label: 'Promoções', count: novidadesData.filter(n => n.categoria === 'promo').length },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setNovidadesFilter(cat.id)}
                className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all ${
                  novidadesFilter === cat.id
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'border border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                }`}
              >
                {cat.label}
                <span className={`rounded-full px-2 py-0.5 text-xs ${
                  novidadesFilter === cat.id ? 'bg-white/25' : 'bg-gray-100'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* News grid */}
          {filteredNovidades.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
              <FiBell className="mx-auto mb-3 text-4xl text-gray-300" />
              <p className="font-medium text-gray-500">Sem novidades nesta categoria.</p>
              <p className="mt-1 text-sm text-gray-400">Volta em breve para ver o que há de novo.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredNovidades.map((novidade) => {
                const style = novidadeStyle(novidade.categoria);
                return (
                  <article
                    key={novidade.id}
                    className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${style.bg}`}>
                        <div className="text-xl">{style.icon}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}>
                          {style.badge}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <FiClock className="text-[10px]" />
                          {formatRelativeDate(novidade.published_at)}
                        </span>
                      </div>
                    </div>
                    <h3 className="mb-2 text-base font-semibold text-gray-900 group-hover:text-indigo-600">
                      {novidade.titulo}
                    </h3>
                    <p className="mb-4 flex-1 text-sm leading-relaxed text-gray-600 line-clamp-3">
                      {novidade.resumo}
                    </p>
                    {novidade.link && (
                      <a
                        href={novidade.link}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                      >
                        Saber mais <FiExternalLink className="text-xs" />
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          {/* Feature Voting */}
          <div className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-indigo-100/40 blur-2xl" />
            <div className="relative">
              <div className="mb-4 flex items-start gap-3">
                <div className="rounded-xl bg-indigo-600 p-3 text-white shadow-md">
                  <FiThumbsUp className="text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Vota nas próximas funcionalidades</h3>
                  <p className="text-sm text-gray-600">Ajuda-nos a decidir o que construir a seguir no Pagali.</p>
                </div>
              </div>

              {featuresData.length === 0 ? (
                <div className="rounded-xl bg-white/60 p-6 text-center text-sm text-gray-500">
                  Sem propostas abertas a votação neste momento.
                </div>
              ) : (
                <div className="space-y-2">
                  {featuresData.map((feature) => {
                    const maxVotos = Math.max(...featuresData.map(f => f.total_votos), 1);
                    const percent = (feature.total_votos / maxVotos) * 100;
                    return (
                      <div
                        key={feature.id}
                        className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-3 transition-all hover:border-indigo-200 hover:shadow-sm"
                      >
                        <div
                          className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-50 to-purple-50 transition-all"
                          style={{ width: `${percent}%` }}
                        />
                        <div className="relative flex items-center gap-3">
                          <button
                            onClick={async () => {
                              if (feature.votou) return;
                              try {
                                await hubService.votarFeature(feature.id);
                                fetchData();
                              } catch (error) {
                                console.error('Error voting:', error);
                              }
                            }}
                            disabled={!!feature.votou}
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-all ${
                              feature.votou
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-600 hover:text-white'
                            }`}
                            aria-label="Votar nesta funcionalidade"
                          >
                            <FiThumbsUp />
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-medium text-gray-800">{feature.titulo}</p>
                            {feature.descricao && (
                              <p className="truncate text-xs text-gray-500">{feature.descricao}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-indigo-600">{feature.total_votos}</p>
                            <p className="text-[10px] uppercase tracking-wide text-gray-400">votos</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'recursos' && (() => {
        const recursoIconMap: Record<string, React.ReactNode> = {
          'message-circle': <FiMessageCircle className="text-xl text-blue-600" />,
          'play-circle': <FiPlayCircle className="text-xl text-purple-600" />,
          'book-open': <FiBookOpen className="text-xl text-green-600" />,
          'help-circle': <FiHelpCircle className="text-xl text-indigo-600" />,
        };
        const recursoBgMap: Record<string, string> = {
          'message-circle': 'bg-blue-100',
          'play-circle': 'bg-purple-100',
          'book-open': 'bg-green-100',
          'help-circle': 'bg-indigo-100',
        };
        const quickActions = recursosData.filter(r => r.tipo === 'quick_action');
        const faqs = recursosData.filter(r => r.tipo === 'faq');
        const contactos = recursosData.filter(r => r.tipo === 'contacto');

        return (
          <div className="space-y-6">
            {/* Quick Actions (from API) */}
            {quickActions.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                {quickActions.map((qa) => {
                  const content = (
                    <>
                      <div className={`p-3 rounded-xl w-fit mb-3 ${recursoBgMap[qa.icone || ''] || 'bg-indigo-100'}`}>
                        {recursoIconMap[qa.icone || ''] || <FiHelpCircle className="text-xl text-indigo-600" />}
                      </div>
                      <h3 className="font-semibold text-gray-900 mb-1">{qa.titulo}</h3>
                      <p className="text-sm text-gray-500">{qa.descricao}</p>
                    </>
                  );
                  const baseClass = "bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer block";
                  // Special handling for Lyra chatbot
                  if (qa.url === '#lyra') {
                    return (
                      <button key={qa.id} className={baseClass + ' text-left'} onClick={() => window.dispatchEvent(new Event('open-lyra'))}>
                        {content}
                      </button>
                    );
                  }
                  // Internal routes use SPA navigation; external use <a>
                  if (qa.url?.startsWith('/')) {
                    return (
                      <button key={qa.id} className={baseClass + ' text-left'} onClick={() => navigate(qa.url!)}>
                        {content}
                      </button>
                    );
                  }
                  return qa.url ? (
                    <a key={qa.id} href={qa.url} className={baseClass}>{content}</a>
                  ) : (
                    <div key={qa.id} className={baseClass}>{content}</div>
                  );
                })}
              </div>
            )}

            {/* FAQ Section (from API) */}
            {faqs.length > 0 && (
              <div className="bg-white rounded-xl p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Perguntas Frequentes</h3>
                <div className="space-y-3">
                  {faqs.map((faq) => (
                    <details key={faq.id} className="group">
                      <summary className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100">
                        <span className="font-medium text-gray-900">{faq.titulo}</span>
                        <FiChevronRight className="text-gray-400 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="p-3 text-sm text-gray-600 space-y-2">
                        <p className="font-medium text-gray-700">{faq.descricao}</p>
                        {faq.conteudo && <p className="whitespace-pre-line">{faq.conteudo}</p>}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info (from API) */}
            {contactos.length > 0 && (
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
                <h3 className="font-semibold mb-3">Precisa de mais ajuda?</h3>
                <div className="space-y-2 text-sm">
                  {contactos.map((c) => (
                    <p key={c.id}>
                      <span className="opacity-80">{c.titulo}:</span>{' '}
                      {c.url ? (
                        <a href={c.url} className="underline hover:no-underline">{c.descricao}</a>
                      ) : (
                        <span>{c.descricao}</span>
                      )}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {recursosData.length === 0 && (
              <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
                <FiHelpCircle className="mx-auto mb-3 text-4xl text-gray-300" />
                <p className="text-gray-500">Recursos ainda não disponíveis. Volta em breve.</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default Hub;
